import React, { useMemo } from 'react';
import get from 'lodash/get';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import Button from '../../../Button';
import Popup from '../../../common/Popup';
import ShowMore from '../../../common/ShowMore';
import SimpleAvatar from '../../../common/SimpleAvatar';
import { isDesktop } from '../../../../utils/mobileHelpers';
import ExpandableListItem from '../../../ExpandableListItem';
import { useProgramConsents } from '../../../Programs/hooks';
import ProgramSubscription from '../../../Programs/ProgramSubscription';
import { targetUserIdSelector } from '../../../../redux/context/selectors';
import { selectScheduledTasks } from '../../../../redux/healthboard/selectors';
import useProgramSubscription from '../../../../hooks/program/useProgramSubscription';
import { actions as acceptInvitationActions } from '../../../../redux/programs/acceptInvitation';
import { actions as declineInvitationActions } from '../../../../redux/programs/declineInvitation';

import styles from './styles.styl';

function ProgramDetailsPopup({ item, opened, onClose, formatMessage }) {
  const dispatch = useDispatch();

  const todos = useSelector(selectScheduledTasks);
  const userId = useSelector(targetUserIdSelector);

  const { program, itemId } = { ...item };
  const {
    id,
    name,
    logo = {},
    description,
    withSubscription,
    organizationName: organization,
    organization: { id: organizationId },
  } = {
    ...program,
  };

  const {
    handleChangeMembershipDuration,
    isSubscriptionStatusPending,
    moreThanOneSubscriptionOption,
    prepaidByProvider,
    selectedMembershipId,
    subscriptionsSetting,
  } = useProgramSubscription({ program });

  const payload = {
    userId,
    organizationId,
    todoId: itemId,
    withSubscription,
    programName: name,
    prepaidByProvider,
    subscriptionsSetting,
    programId: program.id,
    invitationId: item.id,
  };

  const { consents = [] } = useMemo(
    () =>
      get(todos.find(({ id: entityId }) => entityId === itemId), 'payload', {}),
    [todos],
  );

  const { list, proxify } = useProgramConsents({
    program,
    consents,
    formatMessage,
    todo: { itemId },
  });

  const title = formatMessage({ id: 'program.invite.title' }, { name });

  const icon = useMemo(
    () =>
      logo.thumbMin && (
        <SimpleAvatar
          size={32}
          text={name}
          className="mr2"
          src={logo.thumbMin}
        />
      ),
    [logo],
  );

  const subtitle = useMemo(
    () =>
      [
        formatMessage({ id: 'healthboard.program.card.type' }),
        organization,
      ].join(', '),
    [organization],
  );

  return (
    <Popup
      strictClose
      opened={opened}
      onClose={onClose}
      width={isDesktop() ? 'small' : 'medium'}
      title={formatMessage({ id: 'program.invite.details.title' })}
    >
      <ExpandableListItem
        locked
        key={id}
        data-id={id}
        header={{ subtitle, title, icon: icon || 'program' }}
      >
        <div className={styles.section}>
          <p className="f-12">
            <span className={styles.organization}>{organization} </span>
            {formatMessage(
              { id: 'program.invite.details.titleInvite' },
              { name },
            )}
          </p>
        </div>

        <If condition={Boolean(description)}>
          <div className={styles.section}>
            <p className={styles.title}>
              {formatMessage({ id: 'program.invite.details.descriptionTitle' })}
            </p>

            <ShowMore
              minHeight={12 * 3}
              more={formatMessage({ id: 'common.readMore' })}
              less={formatMessage({ id: 'common.collapse' })}
            >
              <p className={styles.description}>{description}</p>
            </ShowMore>
          </div>
        </If>

        <If condition={Boolean(consents.length)}>
          <div className={styles.section}>{list}</div>
        </If>

        <ProgramSubscription
          formatMessage={formatMessage}
          handleChangeMembershipDuration={handleChangeMembershipDuration}
          isSubscriptionStatusPending={isSubscriptionStatusPending}
          prepaidByProvider={prepaidByProvider}
          program={program}
          selectedMembershipId={selectedMembershipId}
          subscriptionsSetting={subscriptionsSetting}
        />

        <div className={styles.section}>
          <div className={styles.buttonWrapper}>
            <Button
              color="primary"
              className={styles.button}
              disabled={moreThanOneSubscriptionOption && !selectedMembershipId}
              onClick={proxify(() => {
                dispatch(acceptInvitationActions.init(payload));
              })}
            >
              {formatMessage({
                id:
                  withSubscription && !prepaidByProvider
                    ? 'program.membership.pay'
                    : 'common.join',
              })}
            </Button>

            <Button
              color="accent"
              className={styles.button}
              onClick={() => {
                dispatch(declineInvitationActions.init(payload));
              }}
            >
              {formatMessage({ id: 'common.decline' })}
            </Button>
          </div>
        </div>
      </ExpandableListItem>
    </Popup>
  );
}

ProgramDetailsPopup.propTypes = {
  item: PropTypes.object,
  opened: PropTypes.bool,
  onClose: PropTypes.func,
  formatMessage: PropTypes.func,
};

export default ProgramDetailsPopup;



// WEBPACK FOOTER //
// ./src/components/Healthboard/Components/ProgramDetailsPopup/index.jsx